import { useCallback, useRef } from 'react';
import { useProjectStore } from '@/store/projectStore';
import { createOverlay } from '@/utils/overlayFactory';
import type { Tool, VideoMetadata } from '@/types';
import clsx from 'clsx';
import {
  MousePointer2,
  Square,
  RectangleHorizontal,
  Circle,
  Hexagon,
  Type,
  Image,
  Sparkles,
  Undo2,
  Redo2,
  Save,
  FolderOpen,
  FilePlus,
  Download,
  Trash2,
  Copy,
  ChevronUp,
  ChevronDown,
  ChevronsUp,
  ChevronsDown,
  Scissors,
  Sun,
  Moon,
  Layers,
  HelpCircle,
} from 'lucide-react';
import { useThemeStore } from '@/store/themeStore';
import { PresetTemplates } from '@/components/PresetTemplates';
import { QRCodeGenerator } from '@/components/QRCodeGenerator';

interface ToolButtonProps {
  tool: Tool;
  icon: React.ReactNode;
  label: string;
  description?: string;
  isActive: boolean;
  onClick: () => void;
}

const ToolButton = ({ icon, label, description, isActive, onClick }: ToolButtonProps) => {
  const theme = useThemeStore((state) => state.theme);
  
  return (
    <button
      onClick={onClick}
      className={clsx(
        'group relative flex flex-col items-center justify-center gap-1 p-2 rounded-lg transition-all duration-200',
        isActive
          ? theme === 'dark'
            ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/30 scale-105'
            : 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25 scale-105'
          : 'text-editor-text-muted hover:bg-editor-hover hover:text-editor-text hover:scale-102'
      )}
      title={description || label}
    >
      {icon}
      <span className="text-[10px] font-medium">{label}</span>
      {/* Enhanced tooltip */}
      {description && (
        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
          <div className="bg-editor-surface border border-editor-border rounded-lg px-3 py-2 shadow-xl whitespace-nowrap">
            <p className="text-xs font-medium text-editor-text">{label}</p>
            <p className="text-[10px] text-editor-text-muted">{description}</p>
          </div>
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-editor-surface" />
        </div>
      )}
    </button>
  );
};

export const Toolbar = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  
  const selectedTool = useProjectStore((state) => state.selectedTool);
  const setSelectedTool = useProjectStore((state) => state.setSelectedTool);
  const video = useProjectStore((state) => state.project.video);
  const overlays = useProjectStore((state) => state.project.overlays);
  const selectedIds = useProjectStore((state) => state.selectionState.selectedIds);
  const currentTime = useProjectStore((state) => state.playerState.currentTime);
  const historyIndex = useProjectStore((state) => state.historyIndex);
  const historyLength = useProjectStore((state) => state.history.length);
  
  const trimRange = useProjectStore((state) => state.project.trimRange);
  
  const newProject = useProjectStore((state) => state.newProject);
  const setVideo = useProjectStore((state) => state.setVideo);
  const addOverlay = useProjectStore((state) => state.addOverlay);
  const deleteSelectedOverlays = useProjectStore((state) => state.deleteSelectedOverlays);
  const duplicateOverlay = useProjectStore((state) => state.duplicateOverlay);
  const reorderOverlay = useProjectStore((state) => state.reorderOverlay);
  const undo = useProjectStore((state) => state.undo);
  const redo = useProjectStore((state) => state.redo);
  const openExportPanel = useProjectStore((state) => state.openExportPanel);
  const loadProject = useProjectStore((state) => state.loadProject);
  const saveProject = useProjectStore((state) => state.saveProject);
  const setTrimRange = useProjectStore((state) => state.setTrimRange);
  const toggleTrimEnabled = useProjectStore((state) => state.toggleTrimEnabled);
  
  const isTrimEnabled = trimRange?.enabled ?? false;
  
  // Theme
  const theme = useThemeStore((state) => state.theme);
  const toggleTheme = useThemeStore((state) => state.toggleTheme);
  
  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < historyLength - 1;
  
  const tools: { tool: Tool; icon: React.ReactNode; label: string; description: string }[] = [
    { tool: 'select', icon: <MousePointer2 size={20} />, label: 'Select', description: 'Select & move overlays' },
    { tool: 'rectangle', icon: <Square size={20} />, label: 'Rectangle', description: 'Add rectangle overlay' },
    { tool: 'rounded-rectangle', icon: <RectangleHorizontal size={20} />, label: 'Rounded', description: 'Add rounded rectangle' },
    { tool: 'circle', icon: <Circle size={20} />, label: 'Circle', description: 'Add circle overlay' },
    { tool: 'polygon', icon: <Hexagon size={20} />, label: 'Polygon', description: 'Add polygon shape' },
    { tool: 'text', icon: <Type size={20} />, label: 'Text', description: 'Add text watermark' },
    { tool: 'image', icon: <Image size={20} />, label: 'Logo', description: 'Add logo/image watermark' },
    { tool: 'blur', icon: <Sparkles size={20} />, label: 'Blur', description: 'Blur/pixelate region' },
  ];
  
  // Handle video import
  const handleVideoImport = useCallback(() => {
    fileInputRef.current?.click();
  }, []);
  
  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Create object URL for the video
    const url = URL.createObjectURL(file);
    
    // Get video metadata
    const videoEl = document.createElement('video');
    videoEl.src = url;
    
    await new Promise<void>((resolve) => {
      videoEl.onloadedmetadata = () => resolve();
    });
    
    const metadata: VideoMetadata = {
      path: url,
      name: file.name,
      duration: videoEl.duration,
      width: videoEl.videoWidth,
      height: videoEl.videoHeight,
      fps: 30, // Default, could be determined from more advanced API
      codec: 'unknown',
      fileSize: file.size,
    };
    
    // Pass the file blob for audio extraction during export
    setVideo(metadata, file);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [setVideo]);
  
  // Handle image import for logo overlay
  const handleImageImport = useCallback(() => {
    if (!video) return;
    imageInputRef.current?.click();
  }, [video]);
  
  const handleImageChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !video) return;
    
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      
      // Get image dimensions
      const img = new window.Image();
      img.onload = () => {
        // Scale image to fit reasonably in video
        const maxSize = Math.min(video.width, video.height) * 0.3;
        let width = img.width;
        let height = img.height;
        
        if (width > maxSize || height > maxSize) {
          const scale = maxSize / Math.max(width, height);
          width *= scale;
          height *= scale;
        }
        
        const overlay = createOverlay('image', {
          bounds: {
            x: video.width / 2 - width / 2,
            y: video.height / 2 - height / 2,
            width,
            height,
          },
          startTime: 0,
          endTime: video.duration, // Overlay spans entire video
          zIndex: overlays.length,
          src: dataUrl,
          originalWidth: img.width,
          originalHeight: img.height,
        });
        
        addOverlay(overlay);
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
    
    // Reset file input
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  }, [video, currentTime, overlays.length, addOverlay]);
  
  // Handle tool selection
  const handleToolSelect = useCallback((tool: Tool) => {
    if (tool === 'image') {
      handleImageImport();
    } else {
      setSelectedTool(tool);
    }
  }, [setSelectedTool, handleImageImport]);
  
  // Handle save project
  const handleSaveProject = useCallback(() => {
    const project = saveProject();
    const blob = new Blob([JSON.stringify(project, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project.name.replace(/\s+/g, '_')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [saveProject]);
  
  // Handle load project
  const handleLoadProject = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      
      const text = await file.text();
      try {
        const project = JSON.parse(text);
        loadProject(project);
      } catch {
        alert('Invalid project file');
      }
    };
    input.click();
  }, [loadProject]);
  
  // Handle trim toggle
  const handleTrimToggle = useCallback(() => {
    if (!video) return;
    
    if (!trimRange) {
      // Initialize trim range
      setTrimRange({
        startTime: 0,
        endTime: video.duration,
        enabled: true,
      });
    } else {
      toggleTrimEnabled();
    }
  }, [video, trimRange, setTrimRange, toggleTrimEnabled]);
  
  return (
    <div className="flex items-center justify-between h-14 px-4 glass-panel border-b border-white/5">
      {/* Left section - Branding + File actions */}
      <div className="flex items-center gap-3">
        <div className="pr-3 border-r border-white/10 flex items-center gap-2">
          {/* App icon */}
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
            <Layers size={20} className="text-white" />
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-1.5">
              <h1 className="text-lg font-bold bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent leading-tight">Overlay Studio</h1>
              <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-white/10 border border-white/15 text-editor-text-muted tracking-wide select-none">v2.0</span>
            </div>
            <span className="text-[9px] text-editor-text-muted tracking-wide">Video Watermark & Overlay Editor</span>
          </div>
        </div>
        
        <button
          onClick={newProject}
          className="btn btn-ghost btn-icon"
          title="New Project"
        >
          <FilePlus size={18} />
        </button>
        <button
          onClick={handleLoadProject}
          className="btn btn-ghost btn-icon"
          title="Open Project"
        >
          <FolderOpen size={18} />
        </button>
        <button
          onClick={handleSaveProject}
          className="btn btn-ghost btn-icon"
          title="Save Project"
          disabled={!video}
        >
          <Save size={18} />
        </button>
        
        <div className="divider" />
        
        <button
          onClick={handleVideoImport}
          className={clsx(
            "text-sm transition-all duration-300",
            !video 
              ? "btn btn-primary animate-pulse shadow-lg shadow-indigo-500/40 ring-2 ring-indigo-400/50 ring-offset-2 ring-offset-transparent" 
              : "btn btn-secondary"
          )}
        >
          {!video && <span className="mr-1">▶</span>}
          Import Video
        </button>
        
        {/* Preset Templates - Quick watermark templates */}
        <PresetTemplates />
        
        {/* QR Code Generator */}
        <QRCodeGenerator />
        
        {/* Trim Button - Orange/Amber color */}
        <button
          onClick={handleTrimToggle}
          className={clsx(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
            !video 
              ? 'bg-editor-surface text-editor-text-dim cursor-not-allowed opacity-50'
              : isTrimEnabled
                ? 'bg-orange-500 text-white hover:bg-orange-600 shadow-lg shadow-orange-500/25'
                : 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 border border-orange-500/50'
          )}
          disabled={!video}
          title={isTrimEnabled ? 'Disable Trim Mode' : 'Enable Trim Mode'}
        >
          <Scissors size={16} />
          {isTrimEnabled ? 'Trim On' : 'Trim'}
        </button>
        
        {/* Export Button - Moved here for visibility */}
        <button
          onClick={openExportPanel}
          className="btn btn-primary"
          disabled={!video}
        >
          <Download size={16} className="mr-1" />
          Export
        </button>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/mov,video/avi,video/mkv,video/*"
          className="hidden"
          onChange={handleFileChange}
        />
        
        <input
          ref={imageInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp,image/*"
          className="hidden"
          onChange={handleImageChange}
        />
      </div>
      
      {/* Center section - Overlay Tools */}
      <div className="flex items-center gap-2">
        {/* Section Label */}
        <div className="flex flex-col items-center justify-center px-2">
          <span className="text-[10px] font-semibold text-editor-accent uppercase tracking-wider">Overlay</span>
          <span className="text-[9px] text-editor-muted">Tools</span>
        </div>
        
        {/* Tool buttons */}
        <div className="flex items-center gap-1 bg-editor-surface rounded-xl p-1">
          {/* Select tool */}
          <ToolButton
            key={tools[0].tool}
            tool={tools[0].tool}
            icon={tools[0].icon}
            label={tools[0].label}
            description={tools[0].description}
            isActive={selectedTool === tools[0].tool}
            onClick={() => handleToolSelect(tools[0].tool)}
          />
          
          {/* Divider */}
          <div className="w-px h-6 bg-editor-border/50 mx-1" />
          
          {/* Drawing/Watermark tools */}
          {tools.slice(1).map((t) => (
            <ToolButton
              key={t.tool}
              tool={t.tool}
              icon={t.icon}
              label={t.label}
              description={t.description}
              isActive={selectedTool === t.tool}
              onClick={() => handleToolSelect(t.tool)}
            />
          ))}
        </div>
      </div>
      
      {/* Right section - Actions */}
      <div className="flex items-center gap-1">
        {/* Undo/Redo */}
        <button
          onClick={undo}
          className="btn btn-ghost btn-icon"
          title="Undo (Ctrl+Z)"
          disabled={!canUndo}
        >
          <Undo2 size={18} />
        </button>
        <button
          onClick={redo}
          className="btn btn-ghost btn-icon"
          title="Redo (Ctrl+Y)"
          disabled={!canRedo}
        >
          <Redo2 size={18} />
        </button>
        
        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="btn btn-ghost btn-icon relative overflow-hidden group"
          title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          <div className="relative w-[18px] h-[18px]">
            <Sun 
              size={18} 
              className={clsx(
                "absolute inset-0 transition-all duration-300",
                theme === 'dark' 
                  ? "rotate-0 scale-100 opacity-100" 
                  : "-rotate-90 scale-0 opacity-0"
              )} 
            />
            <Moon 
              size={18} 
              className={clsx(
                "absolute inset-0 transition-all duration-300",
                theme === 'light' 
                  ? "rotate-0 scale-100 opacity-100" 
                  : "rotate-90 scale-0 opacity-0"
              )} 
            />
          </div>
        </button>
        
        {/* Keyboard Shortcuts Help */}
        <button
          onClick={() => {
            // Dispatch custom event to open keyboard shortcuts modal
            window.dispatchEvent(new KeyboardEvent('keydown', { key: '?', shiftKey: true }));
          }}
          className="btn btn-ghost btn-icon"
          title="Keyboard Shortcuts (?)"
        >
          <HelpCircle size={18} />
        </button>
        
        <div className="divider" />
        
        {/* Selection actions */}
        <button
          onClick={() => selectedIds[0] && duplicateOverlay(selectedIds[0])}
          className="btn btn-ghost btn-icon"
          title="Duplicate"
          disabled={selectedIds.length !== 1}
        >
          <Copy size={18} />
        </button>
        <button
          onClick={deleteSelectedOverlays}
          className="btn btn-ghost btn-icon"
          title="Delete (Del)"
          disabled={selectedIds.length === 0}
        >
          <Trash2 size={18} />
        </button>
        
        {/* Layer order */}
        <button
          onClick={() => selectedIds[0] && reorderOverlay(selectedIds[0], 'top')}
          className="btn btn-ghost btn-icon"
          title="Bring to Front (Top Layer)"
          disabled={selectedIds.length !== 1}
        >
          <ChevronsUp size={18} />
        </button>
        <button
          onClick={() => selectedIds[0] && reorderOverlay(selectedIds[0], 'up')}
          className="btn btn-ghost btn-icon"
          title="Bring Forward"
          disabled={selectedIds.length !== 1}
        >
          <ChevronUp size={18} />
        </button>
        <button
          onClick={() => selectedIds[0] && reorderOverlay(selectedIds[0], 'down')}
          className="btn btn-ghost btn-icon"
          title="Send Backward"
          disabled={selectedIds.length !== 1}
        >
          <ChevronDown size={18} />
        </button>
        <button
          onClick={() => selectedIds[0] && reorderOverlay(selectedIds[0], 'bottom')}
          className="btn btn-ghost btn-icon"
          title="Send to Back (Bottom Layer)"
          disabled={selectedIds.length !== 1}
        >
          <ChevronsDown size={18} />
        </button>
        
      </div>
    </div>
  );
};

